import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { applyPublicTenant } from '@/utils/publicTenant';
import { Button } from '@/components/ui/button';
import { CheckCircle2, CalendarClock, Wrench, Loader2, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicWorkshopAppointmentPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // 'confirm' | 'reschedule'

  const load = useCallback(async () => {
    setLoading(true);
    applyPublicTenant();
    try {
      const r = await axios.get(`${API}/public/workshop/appointment/${token}`);
      setData(r.data);
      if (['confirm', 'reschedule'].includes(r.data.customer_response)) setDone(r.data.customer_response);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Termin nicht gefunden.');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const respond = async (action) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/workshop/appointment/${token}/respond`, { action });
      setDone(action);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Übermittlung fehlgeschlagen'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" /> Lädt…</div>;
  if (error) return <div className="min-h-screen flex items-center justify-center p-6 text-center text-slate-600" data-testid="appointment-public-error">{error}</div>;

  const p = data.practice || {};
  const timeStr = data.scheduled_start ? `${data.scheduled_start}${data.scheduled_end ? `–${data.scheduled_end}` : ''} Uhr` : '';

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="appointment-public-page">
      <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="bg-orange-600 text-white px-6 py-5">
          {p.name ? <div className="text-sm opacity-90">{p.name}</div> : null}
          <div className="text-xl font-bold flex items-center gap-2"><Wrench size={22} /> Ihr Werkstatt-Termin</div>
          {data.plate ? <div className="text-sm opacity-90 mt-1">{data.plate}</div> : null}
        </div>

        <div className="p-6 space-y-5">
          <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 space-y-2" data-testid="appointment-details">
            <div className="flex items-center gap-2 text-slate-800 font-semibold"><CalendarClock size={18} className="text-orange-600" />{data.scheduled_date_fmt}{timeStr ? ` · ${timeStr}` : ''}</div>
            <div className="text-sm text-slate-600">{data.service}</div>
            {p.address ? <div className="flex items-start gap-2 text-sm text-slate-600"><MapPin size={15} className="mt-0.5 text-slate-400" />{p.address}</div> : null}
          </div>

          {done ? (
            <div className={`rounded-xl p-5 text-center ${done === 'confirm' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`} data-testid="appointment-done">
              <CheckCircle2 className="mx-auto mb-2" />
              <div className="font-semibold">
                {done === 'confirm' ? 'Termin bestätigt – vielen Dank!' : 'Danke – wir melden uns zur Verschiebung bei Ihnen.'}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-600 text-center">Passt der Termin für Sie?</p>
              <div className="flex gap-3">
                <Button disabled={submitting} onClick={() => respond('confirm')} data-testid="appointment-confirm-btn" className="flex-1 bg-green-600 hover:bg-green-700">
                  {submitting ? <Loader2 className="animate-spin" /> : <><CheckCircle2 className="mr-2 h-4 w-4" />Termin bestätigen</>}
                </Button>
                <Button disabled={submitting} onClick={() => respond('reschedule')} data-testid="appointment-reschedule-btn" variant="outline" className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50">
                  <CalendarClock className="mr-2 h-4 w-4" />Verschieben anfragen
                </Button>
              </div>
              {p.phone ? <p className="text-xs text-slate-400 text-center">Fragen? Rufen Sie uns an: {p.phone}</p> : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicWorkshopAppointmentPage;
